import {
  vArray,
  vBoolean,
  vCheck,
  vCoerceArray,
  vCountryAny,
  vMaxLength,
  vMinLength,
  vObject,
  vObjectId,
  vOmit,
  vOptional,
  type vOutput,
  vPicklist,
  vPipe,
  vString,
  vTransform,
} from '../validation/index.ts'
import { billToSchema } from './account-bill-to.ts'
import { type Workflow, workflows } from './common.ts'
import { baseCustomsBrokerSchema } from './customs-broker.ts'
import { deliveryOptionSchema } from './delivery-option.ts'
import { emergencyContactSchema } from './emergency-contact.ts'
import type { FuelPriceProviderCode } from './fuel.ts'
import { loadEquipmentSchema } from './load-equipment.ts'
import { loadHandlingUnitSchema } from './load-handling-unit.ts'
import { loadLocationSchema } from './load-location.ts'
import { loadRefNumSchema } from './ref-num.ts'

export const MAX_LOAD_LOCATIONS = 12

/** Only a pickup-only truckload, which creates no BOL, may be saved without items. */
export const canSkipLoadItems = (load: {
  workflow: Workflow
  createBOL?: boolean
}) => load.workflow === 'truckload' && load.createBOL === false

export const baseLoadBillToSchema = vObject({
  ...vOmit(billToSchema, ['country']).entries,
  country: vCountryAny,
})
export const upsertLoadSchema = vPipe(
  vObject({
    billTo: vOptional(baseLoadBillToSchema),
    refNums: vPipe(
      vOptional(vArray(loadRefNumSchema)),
      vTransform(value => value ?? []),
    ),
    workflow: vPicklist(workflows),
    bolNumber: vOptional(vString()),
    glCode: vOptional(vString()),
    brandId: vOptional(vObjectId()),
    tags: vOptional(vArray(vString())),
    templateId: vOptional(vObjectId()),
    emergencyContact: vOptional(emergencyContactSchema),
    customsBrokers: vOptional(vCoerceArray(baseCustomsBrokerSchema)),
    locations: vPipe(
      vArray(loadLocationSchema),
      vMinLength(2),
      vMaxLength(MAX_LOAD_LOCATIONS),
    ),
    items: vArray(loadHandlingUnitSchema),
    equipment: loadEquipmentSchema,
    rateLocationType: vOptional(vPicklist(['origin', 'destination'])),
    isLiveLoad: vBoolean(),
    schedulePickup: vOptional(vBoolean()),
    createBOL: vOptional(vBoolean()),
    deliveryOption: vOptional(deliveryOptionSchema),
    isFreightCollect: vOptional(vBoolean()),
    quoteRequestId: vString(),
  }),
  vCheck(
    load => load.items.length > 0 || canSkipLoadItems(load),
    'At least one handling unit is required',
  ),
  vCheck(
    load => load.workflow === 'truckload' || load.locations.length === 2,
    'Only truckload shipments may have additional stops',
  ),
)
export type UpsertLoadDTO = vOutput<typeof upsertLoadSchema>
export const loadStatuses = [
  'quoting',
  'booked',
  'canceled',
  'discarded',
] as const
export type LoadStatus = (typeof loadStatuses)[number]

export type LoadFuel = {
  cost: number
  provider: FuelPriceProviderCode
}

export interface LoadPolling {
  invoicePolledAt?: Date
  documentPolledAt?: Date
  documentQueuedAt?: Date
}
